import { useCallback, useRef, useState } from "react";

const DEBUG_EVENT_LIMIT = 30;

/**
 * useDebugLog — manages a timestamped event log for auction debugging.
 *
 * Reads `?debug` from the URL once on mount and gates console output on it.
 * Each event gets a unique numeric ID so React keys never collide.
 *
 * Returns { debugMode, debugEvents, pushDebugEvent }.
 */
export default function useDebugLog() {
  const debugMode = useRef(
    typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).has("debug")
  ).current;

  const nextIdRef = useRef(0);
  const [debugEvents, setDebugEvents] = useState([]);

  const pushDebugEvent = useCallback(
    (message, details) => {
      const timestamp = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
      const detailSuffix = details ? ` (${details})` : "";
      const text = `${timestamp} ${message}${detailSuffix}`;
      const id = nextIdRef.current;
      nextIdRef.current += 1;

      setDebugEvents(current =>
        [{ id, text }, ...current].slice(0, DEBUG_EVENT_LIMIT)
      );

      if (debugMode) {
        console.info(`[Auctioneer] ${text}`);
      }
    },
    [debugMode]
  );

  return { debugMode, debugEvents, pushDebugEvent };
}
